import { useCallback, useEffect, useState } from 'react';

/**
 * State that survives the component being torn down and rebuilt.
 *
 * A multi-step form guarded by a three-minute timer cannot live in `useState`
 * alone. It disappears for reasons the component never sees:
 *
 *   - a background query resolving and re-rendering the shell above it;
 *   - the browser discarding a backgrounded tab under memory pressure and
 *     reloading it when the user returns;
 *   - an accidental refresh.
 *
 * In each case the person has already received a code they cannot request
 * again until it expires, so losing the form means waiting out a timer with
 * nothing on screen explaining why. Session storage keeps it for as long as the
 * tab is open and discards it when the tab closes — which is the right lifetime
 * for a half-finished sign-in.
 */
export function usePersistentState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored === null ? initial : (JSON.parse(stored) as T);
    } catch {
      // Private browsing, a full quota, or a value written by an older version
      // of this code. None of them are worth failing a login over.
      return initial;
    }
  });

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* the form still works in memory; only the recovery is lost */
      }
    },
    [key],
  );

  return [value, update];
}

/** Forget a persisted value — after a completed flow, or on cancel. */
export function clearPersisted(...keys: string[]): void {
  for (const key of keys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* nothing to do */
    }
  }
}

/**
 * A countdown that survives a reload, because it stores *when* the code dies
 * rather than how long is left.
 *
 * Storing the remaining seconds would restart the count on every reload, which
 * would either offer "resend" while the server still refuses it, or show a
 * number that is simply wrong. The deadline is a fact about the code; the
 * seconds remaining are a view of it.
 */
export function useDeadline(key: string): {
  remaining: number;
  start: (seconds: number) => void;
  clear: () => void;
} {
  const [deadline, setDeadline] = usePersistentState<number | null>(key, null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null || deadline <= now) return;
    const timer = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(timer);
  }, [deadline, now]);

  // Re-read the clock when the tab comes back: a timer that was throttled while
  // hidden would otherwise resume from a stale reading and count down slowly.
  useEffect(() => {
    const onVisible = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const remaining = deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));

  return {
    remaining,
    start: (seconds: number) => {
      setDeadline(Date.now() + seconds * 1000);
      setNow(Date.now());
    },
    clear: () => setDeadline(null),
  };
}
